import { defineComponent as _defineComponent } from 'vue'
import type { Maybe } from '@/helpers/types';
import type { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';

import { computed, ref } from 'vue';
import { useElementSize, useMediaQuery } from '@vueuse/core';
import { BackgroundImageSize, SraPlacement } from '@/@types/graphql-types';
import { SingleTitleCampaign } from '@/features/sponsored-recommendations/types';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import PictureComp from '@/components/picture/PictureComp.vue';

type ImageObject = { [key in BackgroundImageSize]: string };

export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationImageSearch',
  props: {
    sponsoredAdCampaign: null,
    lazy: { type: Boolean, default: true },
    additionalContexts: { default: () => [] },
    isCinemaSR: { type: Boolean, default: false },
    showPoster: { type: Boolean, default: false },
    sraPlacement: null
  },
  emits: ["onPosterClickOut", "imageError"],
  setup(__props: any) {

const props = __props as { sponsoredAdCampaign: SingleTitleCampaign, lazy: boolean, additionalContexts: SnowplowContext[], isCinemaSR: boolean, showPoster: boolean, sraPlacement?: SraPlacement };





const campaignNode = computed(() => props.sponsoredAdCampaign.node);
const backgroundImages = computed(() => {
	const imageSizes: ImageObject = {} as ImageObject;

	props.sponsoredAdCampaign?.backgroundImages?.map(options => {
		if (!options?.size) {
			return;
		}

		imageSizes[options.size] = options?.imageURL;
	});

	return imageSizes;
});

// Mirror the CSS rule structure for asset selection:
//   - searchbox  -> measure the SR's own width (container-based)
//   - searchpage -> use viewport (media query)
// srWidth starts at 0 until ResizeObserver fires — fall back to viewport check until then.
const isSmallViewport = useMediaQuery('(max-width: 703px)');
const srRoot = ref<HTMLElement | null>(null);
const { width: srWidth } = useElementSize(srRoot);

const useSmallAsset = computed(() => {
	const isSearchbox = props.sraPlacement === SraPlacement.SearchBox;
	return isSearchbox && srWidth.value > 0 ? srWidth.value <= 703 : isSmallViewport.value;
});

const imageSrc = computed(() => {
	if (useSmallAsset.value && backgroundImages.value?.SMALL) {
		return backgroundImages.value.SMALL;
	}
	return props.sponsoredAdCampaign?.promotionalImageUrl ?? '';
});

const imageSrcAvif = computed(() => imageSrc.value.replace(/\.\w+$/, '.avif'));

const posterPath = computed(() => props.sponsoredAdCampaign.node.content.fullPath);

const posterImageOverride = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAdCampaign?.posterOverride || campaignNode.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

return { __sfc: true,props, campaignNode, backgroundImages, isSmallViewport, srRoot, srWidth, useSmallAsset, imageSrc, imageSrcAvif, posterPath, posterImageOverride, SraPlacement, PictureComp }
}

})